import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, Variants } from 'framer-motion';

interface FaqItem {
  question: string;
  answer: React.ReactNode;
  category: string;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07
    }
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const headingVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const FaqPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('general');
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<FaqItem[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const toggleItem = (index: number) => {
    if (openItems.includes(index)) {
      setOpenItems(openItems.filter(item => item !== index));
    } else {
      setOpenItems([...openItems, index]);
    }
  };

  const faqData: FaqItem[] = [
    // General
    {
      question: 'What is Developer Marketplace?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          Developer Marketplace is a platform where developers can buy and sell digital assets such as UI kits, templates, APIs, plugins, and other development resources. Our marketplace connects creators with developers looking for high-quality tools to accelerate their projects.
        </p>
      ),
      category: 'general'
    },
    {
      question: 'How do I create an account?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          You can create an account by clicking the "Sign Up" button in the top right corner of our website. You'll need to provide your email address, create a password, and verify your email. You can also sign up using your GitHub, Google, or social media accounts for faster registration.
        </p>
      ),
      category: 'general'
    },
    {
      question: 'Is Developer Marketplace free to use?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          Creating an account and browsing the marketplace is completely free. We only charge a commission on sales when you sell an item. Buyers pay only the listed price of the items they purchase, with no additional fees.
        </p>
      ),
      category: 'general'
    },

    // Buying
    {
      question: 'How do I purchase an item?',
      answer: (
        <div>
          <p className="text-white/90 leading-relaxed mb-3">To purchase an item, follow these steps:</p>
          <ol className="list-decimal pl-6 mt-2 mb-3 space-y-2">
            <li className="text-white/90 leading-relaxed">Browse the marketplace or search for the item you want</li>
            <li className="text-white/90 leading-relaxed">Click on the item to view its details</li>
            <li className="text-white/90 leading-relaxed">Click the "Buy" button</li>
            <li className="text-white/90 leading-relaxed">Review your cart and proceed to checkout</li>
            <li className="text-white/90 leading-relaxed">Enter your payment information</li>
            <li className="text-white/90 leading-relaxed">Complete the purchase</li>
          </ol>
          <p className="text-white/90 leading-relaxed mt-2">After purchase, you'll be able to download the item from your account dashboard.</p>
        </div>
      ),
      category: 'buying'
    },
    {
      question: 'What payment methods do you accept?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          We accept credit/debit cards (Visa, Mastercard, American Express), PayPal, and crypto payments via Coinbase Commerce. All transactions are processed securely through our payment partners.
        </p>
      ),
      category: 'buying'
    },
    {
      question: 'Can I get a refund if I\'m not satisfied?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          Yes, we offer a 14-day refund policy for most items if they don't work as described. However, refunds are subject to review and may not be available for certain types of digital goods after download. Please refer to our <Link to="/terms" className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">Refund Policy</Link> for more details.
        </p>
      ),
      category: 'buying'
    },
    {
      question: 'Can I use the items I purchase for commercial projects?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          Usage rights depend on the specific license of each item. Most items on our marketplace come with a standard license that allows for use in a single commercial project. Some items offer extended licenses for broader usage. Always check the license details on the item's page before purchasing.
        </p>
      ),
      category: 'buying'
    },

    // Selling
    {
      question: 'How do I become a seller?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          To become a seller, go to your account settings and click on "Become a Seller." You'll need to complete your profile information, add payment details, and agree to our seller terms. Once your application is approved, you can start listing your items for sale.
        </p>
      ),
      category: 'selling'
    },
    {
      question: 'What can I sell on Developer Marketplace?',
      answer: (
        <div>
          <p className="text-white/90 leading-relaxed mb-3">You can sell a wide range of digital assets for developers, including:</p>
          <ul className="list-disc pl-6 mt-2 mb-3 space-y-2">
            <li className="text-white/90 leading-relaxed">UI Kits and Templates</li>
            <li className="text-white/90 leading-relaxed">Website Templates</li>
            <li className="text-white/90 leading-relaxed">Mobile App Templates</li>
            <li className="text-white/90 leading-relaxed">API Integrations</li>
            <li className="text-white/90 leading-relaxed">Plugins and Extensions</li>
            <li className="text-white/90 leading-relaxed">Code Snippets and Libraries</li>
            <li className="text-white/90 leading-relaxed">Development Tools</li>
            <li className="text-white/90 leading-relaxed">Icon Sets and Graphics</li>
          </ul>
          <p className="text-white/90 leading-relaxed mt-2">All items must comply with our <Link to="/policy" className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">content guidelines</Link>.</p>
        </div>
      ),
      category: 'selling'
    },
    {
      question: 'How much does it cost to sell on Developer Marketplace?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          There's no upfront cost to list items on our marketplace. We operate on a commission model, taking 15% of each sale for standard sellers. High-volume sellers or exclusive authors may qualify for lower commission rates. Earnings are paid out via your chosen payment method once you reach the minimum threshold of $50.
        </p>
      ),
      category: 'selling'
    },
    {
      question: 'How do I receive payments for my sales?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          We process payments on a monthly basis. You can choose to receive your earnings via PayPal, direct bank transfer, or crypto wallet. Payments are processed on the 15th of each month for the previous month's sales, provided you've reached the minimum payout threshold of $50.
        </p>
      ),
      category: 'selling'
    },

    // Technical Support
    {
      question: 'I\'m having trouble downloading an item. What should I do?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          If you're experiencing download issues, try clearing your browser cache and cookies, using a different browser, or disabling any download managers or extensions. If the problem persists, please contact our support team with details of the issue, including your order number and the name of the item.
        </p>
      ),
      category: 'support'
    },
    {
      question: 'How do I get support for an item I purchased?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          For item-specific support, you should first contact the seller directly through the item\'s support tab. Most sellers provide support for their items as specified in their listing. If the seller doesn\'t respond within 48 hours, you can escalate the issue to our support team for assistance.
        </p>
      ),
      category: 'support'
    },
    {
      question: 'I found a security vulnerability. How do I report it?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          We take security seriously. If you discover a security vulnerability, please don\'t disclose it publicly. Instead, email us directly at security@developermarketplace.com with details of the vulnerability. We have a responsible disclosure policy and may offer rewards for significant findings.
        </p>
      ),
      category: 'support'
    },
    {
      question: 'My account has been suspended. What can I do?',
      answer: (
        <p className="text-white/90 leading-relaxed">
          If your account has been suspended, you should have received an email explaining the reason. To appeal, reply to that email or contact our support team with your account details and an explanation. We review all appeals thoroughly and aim to respond within 48 hours.
        </p>
      ),
      category: 'support'
    },
  ];

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const results = faqData.filter(
      item => item.question.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSearchResults(results);
  }, [searchTerm]);

  const filteredFaqs = isSearching
    ? searchResults
    : faqData.filter(item => item.category === activeCategory);

  return (
    <div className="relative min-h-screen overflow-hidden" style={{
      background: 'linear-gradient(135deg, #141019 0%, #1F1A27 40%, #252042 100%)',
    }}>
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-500/10 blur-[170px] rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-pink-500/10 blur-[170px] rounded-full"></div>

      {/* Decorative grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="h-full w-full" style={{
          backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(to right, #ffffff 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInVariants}
              className="inline-block mb-4 px-4 py-1 bg-white/10 backdrop-blur-md rounded-full text-[#F670C7] text-sm font-medium"
            >
              Support Resources
            </motion.div>

            <motion.h1
              initial="hidden"
              animate="visible"
              variants={headingVariants}
              className="text-4xl md:text-5xl font-bold mb-6 text-white"
            >
              Frequently Asked Questions
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="text-white/80 text-lg mb-10 max-w-2xl mx-auto leading-relaxed"
            >
              Find answers to common questions about Developer Marketplace. Can't find what you're looking for?
              <Link to="/contact" className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline ml-1 font-medium">
                Contact our support team
              </Link>.
            </motion.p>
          </div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="relative max-w-2xl mx-auto mb-12"
          >
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search for questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full py-4 pl-10 pr-4 bg-white/10 backdrop-blur-md text-white placeholder-white/50 rounded-xl border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#F670C7]/50 focus:border-transparent transition-all duration-300"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/50 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </motion.div>

          {/* FAQ Categories */}
          {!isSearching && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-wrap justify-center gap-3 mb-12"
            >
              {['general', 'buying', 'selling', 'support'].map((category) => (
                <motion.button
                  key={category}
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setActiveCategory(category);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeCategory === category
                      ? 'bg-gradient-to-r from-[#7F56D9] to-[#F670C7] text-white shadow-lg shadow-[#F670C7]/20'
                      : 'bg-white/10 backdrop-blur-sm text-white/80 hover:text-white hover:bg-white/15 border border-white/10'
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </motion.button>
              ))}
            </motion.div>
          )}

          {/* Search Results Stats */}
          {isSearching && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center mb-8 text-white/80"
            >
              Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchTerm}"
            </motion.div>
          )}

          {/* FAQ Accordion */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="space-y-4"
          >
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white/10 backdrop-blur-md rounded-xl overflow-hidden border border-white/10 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <button
                    className="flex justify-between items-center w-full p-6 text-left focus:outline-none text-white group"
                    onClick={() => toggleItem(index)}
                  >
                    <span className="text-lg font-semibold pr-8 group-hover:text-[#F670C7] transition-colors duration-300">
                      {faq.question}
                    </span>
                    <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      openItems.includes(index)
                        ? 'bg-[#F670C7] rotate-180'
                        : 'bg-white/20 group-hover:bg-white/30'
                    }`}>
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </button>
                  <AnimatePresence>
                    {openItems.includes(index) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="p-6 pt-0 border-t border-white/10">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))
            ) : (
              <motion.div
                variants={itemVariants}
                className="text-center py-12"
              >
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/10">
                  <svg className="w-16 h-16 text-white/40 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <h3 className="text-xl font-bold text-white mb-2">No results found</h3>
                  <p className="text-white/70">
                    We couldn't find any questions matching your search.
                    <br />Try a different search term or browse by category.
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Contact Support */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-20"
          >
            <div className="relative bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-2xl p-10 text-center overflow-hidden border border-white/10 shadow-xl">
              <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>

              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#F670C7]/20 blur-3xl rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#7F56D9]/20 blur-3xl rounded-full transform -translate-x-1/2 translate-y-1/2"></div>

              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-white/10 backdrop-blur-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#F670C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold mb-4 text-white">Still have questions?</h2>
                <p className="text-white/90 leading-relaxed mb-8 max-w-2xl mx-auto text-lg">
                  Our support team is here to help you with any questions or issues you may have.
                  We typically respond within 24 hours on business days.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/contact"
                      className="bg-gradient-to-r from-[#7F56D9] to-[#F670C7] hover:opacity-90 text-white py-3 px-8 rounded-lg transition-all inline-block font-medium shadow-lg shadow-[#F670C7]/20"
                    >
                      Contact Support
                    </Link>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/docs"
                      className="bg-white/10 hover:bg-white/15 text-white py-3 px-8 rounded-lg transition-all inline-block font-medium border border-white/10"
                    >
                      Browse Documentation
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FaqPage;
